import { forwardRef, useEffect, useState } from "react";
import { format, differenceInMinutes } from "date-fns";
import { es } from "date-fns/locale";
import { QRLabelBase } from "./QRLabelBase";
import { api } from "@/lib/apiClient";
import { Skeleton } from "@/components/ui/skeleton";
import { CheckCircle, AlertCircle, Clock, Package } from "lucide-react";
import type { LabelSize, FinalTraceabilityData } from "@/types/labels";

interface FinalTraceabilityLabelProps {
  workOrderId: string;
  shortCode?: string;
  size?: LabelSize;
}

interface ProcessStep {
  name: string;
  icon: "check" | "alert" | "clock";
  value: string;
  detail?: string;
}

export const FinalTraceabilityLabel = forwardRef<HTMLDivElement, FinalTraceabilityLabelProps>(
  ({ workOrderId, shortCode, size = "large" }, ref) => {
     
    const [data, setData] = useState<FinalTraceabilityData | null>(null);
    const [loading, setLoading] = useState(true);
    const [labelCode, setLabelCode] = useState(shortCode || "");

    useEffect(() => {
      const fetchFullTraceability = async () => {
        setLoading(true);
        try {
          // 1. Get work order with relations
          const { data: wo } = await api
            .from("work_orders")
            .select(`
              *,
              product_model:product_models(name),
              customer:billing_customers(legal_name),
              mold:molds(code)
            `)
            .eq("id", workOrderId)
            .single();

          if (!wo) throw new Error("Work order not found");

          // 2. Get plybook execution
          const { data: execution } = await api
            .from("plybook_executions")
            .select(`
              *,
              operator:profiles!plybook_executions_operator_id_fkey(full_name),
              plybook:plybooks(code)
            `)
            .eq("work_order_id", workOrderId)
            .order("completed_at", { ascending: false })
            .limit(1)
            .single();

          // 3. Get plotter cut from ply confirmations
          let plotterCut = null;
          if (execution) {
            const { data: confirmation } = await api
              .from("plybook_ply_confirmations")
              .select(`
                plotter_cut:plotter_cuts(
                  package_code,
                  batch_number,
                  cut_date,
                  created_by:profiles(full_name)
                )
              `)
              .eq("execution_id", execution.id)
              .not("plotter_cut_id", "is", null)
              .limit(1)
              .single();
            plotterCut = confirmation?.plotter_cut;
          }

          // 4. Get raw material info from batch
          let origin = { batch: "N/A", supplier: "N/A", entry_d: "N/A" };
          if (plotterCut?.batch_number) {
            const { data: material } = await api
              .from("material_inventory")
              .select("batch_number, supplier:billing_customers(legal_name), received_date")
              .eq("batch_number", plotterCut.batch_number)
              .limit(1)
              .single();

            if (material) {
              origin = {
                batch: material.batch_number,
                supplier: material.supplier?.legal_name || "N/A",
                entry_d: material.received_date || "N/A",
              };
            }
          }

          // 5. Get process records
          const { data: processes } = await api
            .from("process_records")
            .select(`
              *,
              process:manufacturing_processes(code, name),
              operator:profiles(full_name)
            `)
            .eq("work_order_id", workOrderId)
            .order("started_at", { ascending: true });

          // 6. Get autoclave cycle
          const { data: autoclave } = await api
            .from("autoclave_cycle_molds")
            .select(`
              autoclave_cycle:autoclave_cycles(
                cycle_number,
                temperature_setpoint,
                pressure_setpoint,
                started_at,
                completed_at
              )
            `)
            .eq("work_order_id", workOrderId)
            .limit(1)
            .single();

          // 7. Get quality inspection
          const { data: inspection } = await api
            .from("quality_inspections")
            .select("result, inspector:profiles(full_name)")
            .eq("work_order_id", workOrderId)
            .eq("inspection_type", "final")
            .limit(1)
            .single();

          // 8. Get painting info
          const { data: painting } = await api
            .from("paint_booth_pieces")
            .select(`
              paint_booth:paint_booths(name),
              operator:profiles(full_name),
              color_code
            `)
            .eq("work_order_id", workOrderId)
            .limit(1)
            .single();

          // Build chain
          const lamDuration = execution?.started_at && execution?.completed_at
            ? differenceInMinutes(new Date(execution.completed_at), new Date(execution.started_at))
            : 0;

          const autoDuration = autoclave?.autoclave_cycle?.started_at && autoclave?.autoclave_cycle?.completed_at
            ? differenceInMinutes(
                new Date(autoclave.autoclave_cycle.completed_at),
                new Date(autoclave.autoclave_cycle.started_at)
              )
            : 0;

          // Calculate total time
          let totalTime = 0;
          (processes || []).forEach((p) => {
            if (p.started_at && p.completed_at) {
              totalTime += differenceInMinutes(new Date(p.completed_at), new Date(p.started_at));
            }
          });

          const traceData: FinalTraceabilityData = {
            wo: wo.order_number,
            prod: wo.product_model?.name || "N/A",
            client: wo.customer?.legal_name || "N/A",
            chain: {
              origin,
              cut: {
                pkg: plotterCut?.package_code || "N/A",
                d: plotterCut?.cut_date || "N/A",
                op: plotterCut?.created_by?.full_name || "N/A",
              },
              lam: {
                plybook: execution?.plybook?.code || "N/A",
                op: execution?.operator?.full_name || "N/A",
                dur_m: lamDuration,
              },
              vac: processes?.find((p) => p.process?.code === "VACUUM")
                ? {
                    dur_m: 15,
                    op: processes.find((p) => p.process?.code === "VACUUM")?.operator?.full_name || "N/A",
                  }
                : undefined,
              auto: autoclave?.autoclave_cycle
                ? {
                    cycle: autoclave.autoclave_cycle.cycle_number,
                    temp: autoclave.autoclave_cycle.temperature_setpoint,
                    press: autoclave.autoclave_cycle.pressure_setpoint,
                    dur_m: autoDuration,
                  }
                : undefined,
              demold: processes?.find((p) => p.process?.code === "DESM")
                ? {
                    d: processes.find((p) => p.process?.code === "DESM")?.completed_at || "",
                    op: processes.find((p) => p.process?.code === "DESM")?.operator?.full_name || "N/A",
                  }
                : undefined,
              insp: inspection
                ? {
                    result: inspection.result as "pass" | "fail" | "conditional",
                    inspector: inspection.inspector?.full_name || "N/A",
                  }
                : undefined,
              shop: processes?.find((p) => p.process?.code === "CORT")
                ? {
                    op: processes.find((p) => p.process?.code === "CORT")?.operator?.full_name || "N/A",
                    dur_m: 0,
                  }
                : undefined,
              paint: painting
                ? {
                    booth: painting.paint_booth?.name || "N/A",
                    op: painting.operator?.full_name || "N/A",
                    color: painting.color_code,
                  }
                : undefined,
            },
            total_time_m: totalTime,
            final_insp: inspection?.inspector?.full_name,
          };

          setData(traceData);

          // Get or create label code
          if (!shortCode) {
            const { data: existingLabel } = await api
              .from("label_prints")
              .select("short_code")
              .eq("work_order_id", workOrderId)
              .eq("label_type", "final")
              .eq("is_active", true)
              .order("printed_at", { ascending: false })
              .limit(1)
              .single();

            if (existingLabel) {
              setLabelCode(existingLabel.short_code);
            }
          }
        } catch (err) {
          console.error("Error fetching traceability:", err);
        } finally {
          setLoading(false);
        }
      };

      fetchFullTraceability();
    }, [workOrderId, shortCode]);

    if (loading) {
      return (
        <div style={{ width: "150mm" }}>
          <Skeleton className="h-32 w-full" />
        </div>
      );
    }

    if (!data) {
      return <div className="text-destructive">Error: Trazabilidad no encontrada</div>;
    }

    const displayCode = labelCode || `FIN-${workOrderId.slice(-6).toUpperCase()}`;

    // Build process steps for visualization
    const steps: ProcessStep[] = [
      { name: "Material", icon: "check", value: data.chain.origin.batch, detail: data.chain.origin.supplier },
      { name: "Corte", icon: "check", value: data.chain.cut.pkg },
      { name: "Laminado", icon: "check", value: `${data.chain.lam.dur_m}m`, detail: data.chain.lam.plybook },
    ];

    if (data.chain.vac) steps.push({ name: "Vacio", icon: "check", value: `${data.chain.vac.dur_m}m` });
    if (data.chain.auto) {
      steps.push({
        name: "Autoclave",
        icon: "check",
        value: data.chain.auto.cycle,
        detail: `${data.chain.auto.temp}C/${data.chain.auto.press}bar`,
      });
    }
    if (data.chain.demold) steps.push({ name: "Desmoldeo", icon: "check", value: "OK" });
    if (data.chain.insp) {
      steps.push({
        name: "Inspeccion",
        icon: data.chain.insp.result === "pass" ? "check" : "alert",
        value: data.chain.insp.result.toUpperCase(),
      });
    }
    if (data.chain.shop) steps.push({ name: "Taller", icon: "check", value: "OK" });
    if (data.chain.paint) {
      steps.push({ name: "Pintura", icon: "check", value: data.chain.paint.booth, detail: data.chain.paint.color });
    }

    return (
      <QRLabelBase
        ref={ref}
        shortCode={displayCode}
        size={size}
        title={data.wo}
        subtitle={data.prod}
      >
        <div style={{ fontSize: "inherit" }}>
          {/* Client */}
          <div
            style={{
              backgroundColor: "#ebf4ff",
              padding: "1mm 2mm",
              borderRadius: "1mm",
              marginBottom: "2mm",
              fontWeight: "600",
              color: "#1a365d",
            }}
          >
            {data.client}
          </div>

          {/* Process chain - compact grid */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(40px, 1fr))",
              gap: "1mm",
              marginBottom: "2mm",
            }}
          >
            {steps.map((step, i) => (
              <div
                key={i}
                style={{
                  textAlign: "center",
                  padding: "1mm",
                  backgroundColor: step.icon === "check" ? "#d1fae5" : "#fee2e2",
                  borderRadius: "1mm",
                  fontSize: "6pt",
                }}
              >
                <div style={{ fontWeight: "bold" }}>{step.name}</div>
                <div>{step.value}</div>
                {step.detail && size === "large" && (
                  <div style={{ fontSize: "5pt", color: "#4a5568" }}>{step.detail}</div>
                )}
              </div>
            ))}
          </div>

          {/* Totals */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              padding: "1mm 2mm",
              backgroundColor: "#1a365d",
              color: "white",
              borderRadius: "1mm",
            }}
          >
            <span>
              <Clock style={{ width: "10px", height: "10px", display: "inline", marginRight: "2px" }} />
              Total: {Math.floor(data.total_time_m / 60)}h {data.total_time_m % 60}m
            </span>
            {data.chain.insp?.result === "pass" && (
              <span>
                <CheckCircle style={{ width: "10px", height: "10px", display: "inline", marginRight: "2px" }} />
                APROBADO
              </span>
            )}
          </div>

          {/* Origin traceability */}
          {size === "large" && (
            <div
              style={{
                marginTop: "2mm",
                fontSize: "6pt",
                color: "#4a5568",
                display: "flex",
                justifyContent: "space-between",
              }}
            >
              <span>Lote: {data.chain.origin.batch}</span>
              <span>Proveedor: {data.chain.origin.supplier}</span>
            </div>
          )}
        </div>
      </QRLabelBase>
    );
  }
);

FinalTraceabilityLabel.displayName = "FinalTraceabilityLabel";
